import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, Plus, User, Users, CheckCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/_core/hooks/useAuth";
import { LaborDurationLabel } from "@/components/technician/LaborDurationLabel";
import { hasReliableDailyAssignmentStartMarker } from "@shared/shiftDuration";

interface Technician {
    id: number;
    name: string | null;
    email: string;
    role: string;
}

interface Assignment {
    id: number;
    technicianId: number;
    installationId: number;
    date: string;
    status: string;
    approvalStatus?: string;
    assignmentSource?: string;
    activeStartTime?: Date | string | null;
    shiftDate?: string | Date | null;
    shiftStatus?: string | null;
    shiftTotalMinutes?: number | null;
    shiftActiveStartAt?: Date | string | null;
    totalMinutes?: number | null;
    startTime?: string | null;
}

interface TechniciansTabProps {
    installationId: number;
    assignedTechnicianIds?: number[];
}

export default function TechniciansTab({ installationId, assignedTechnicianIds = [] }: TechniciansTabProps) {
    const { user } = useAuth();
    const [date, setDate] = useState<Date>(new Date());
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [selectedTech, setSelectedTech] = useState<string>("");

    if (user?.role === 'technician') {
        return (
            <div className="p-8 text-center border rounded-lg bg-card mt-4">
                <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                <p className="text-muted-foreground">No tienes permisos para acceder a la gestión de técnicos.</p>
            </div>
        );
    }

    const dateStr = format(date, "yyyy-MM-dd");

    const utils = trpc.useUtils();
    const assignmentsQuery = trpc.dailyAssignments.list.useQuery({
        installationId,
        date: dateStr
    }, { refetchInterval: 15_000 });
    const usersQuery = trpc.users.list.useQuery();

    const assignMutation = trpc.dailyAssignments.assign.useMutation({
        onSuccess: () => {
            utils.dailyAssignments.list.invalidate({ installationId, date: dateStr });
            setIsAssignDialogOpen(false);
            setSelectedTech("");
            toast.success("Técnico asignado");
        }
    });

    const deleteMutation = trpc.dailyAssignments.delete.useMutation({
        onSuccess: () => {
            utils.dailyAssignments.list.invalidate({ installationId, date: dateStr });
            toast.success("Asignación eliminada");
        },
        onError: (error) => { toast.error(error.message || "No se pudo eliminar la asignación"); },
    });

    const technicians = (usersQuery.data?.filter((u: Technician) => u.role === 'technician') || []) as Technician[];

    // Filter out already assigned techs
    const availableTechs = technicians.filter((t: Technician) =>
        !assignmentsQuery.data?.some((a: Assignment) => a.technicianId === t.id)
    );

    const handleAssign = () => {
        if (!selectedTech) return;
        assignMutation.mutate({
            installationId,
            technicianId: parseInt(selectedTech),
            date: dateStr
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'assigned': return <Badge variant="outline" className="bg-gray-100">Asignado</Badge>;
            case 'working': return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 animate-pulse">Trabajando</Badge>;
            case 'paused': return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">Pausado</Badge>;
            case 'completed': return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Jornada terminada</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getUserName = (id: number) => technicians.find((t: Technician) => t.id === id)?.name || technicians.find((t: Technician) => t.id === id)?.email || "Desconocido";

    // Resolve assigned technician names
    const assignedTechs = technicians.filter((t: Technician) => assignedTechnicianIds.includes(t.id));

    return (
        <div className="space-y-4">
        {/* Section 1: Assigned to project (planning) */}
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Técnicos asignados a la obra
                </CardTitle>
                <CardDescription>Asignación permanente de planificación. Se gestiona desde el formulario de la instalación.</CardDescription>
            </CardHeader>
            <CardContent>
                {assignedTechs.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg border-dashed">
                        <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                        <p className="text-muted-foreground">No hay técnicos asignados a esta obra.</p>
                        <p className="text-xs text-muted-foreground mt-1">Asígnalos desde el formulario de edición de la instalación.</p>
                    </div>
                ) : (
                    <div className="grid gap-2">
                        {assignedTechs.map((tech: Technician) => (
                            <div key={tech.id} className="flex min-w-0 flex-wrap items-center gap-3 p-3 border rounded-lg bg-card">
                                <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                                    {(tech.name || tech.email || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="break-words font-medium">{tech.name || tech.email}</p>
                                    <p className="break-all text-xs text-muted-foreground">{tech.email}</p>
                                </div>
                                <Badge variant="outline" className="ml-auto max-w-full shrink-0">Planificación</Badge>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>

        {/* Section 2: Daily control (execution) */}
        <Card>
            <CardHeader className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Control de Técnicos Diario
                    </CardTitle>
                    <CardDescription>Gestiona la asistencia y trabajo diario en la instalación</CardDescription>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" className={cn("w-full sm:w-[240px] justify-start text-left font-normal", !date && "text-muted-foreground")}>
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {date ? format(date, "PPP", { locale: es }) : <span>Seleccionar fecha</span>}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(d) => d && setDate(d)}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>

                    <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                        <DialogTrigger asChild>
                            <Button size="sm" className="shrink-0"><Plus className="w-4 h-4 mr-2" /> Asignar</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Asignar Técnico para hoy</DialogTitle>
                            </DialogHeader>
                            <div className="py-4">
                                <Select onValueChange={setSelectedTech} value={selectedTech}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Seleccionar técnico..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableTechs.map((t: Technician) => (
                                            <SelectItem key={t.id} value={t.id.toString()}>{t.name || t.email}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button onClick={handleAssign} disabled={!selectedTech || assignMutation.isPending}>Asignar</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {assignmentsQuery.isLoading ? (
                        <p className="text-center text-muted-foreground py-8">Cargando...</p>
                    ) : assignmentsQuery.data?.length === 0 ? (
                        <div className="text-center py-12 border rounded-lg border-dashed">
                            <User className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                            <p className="text-muted-foreground">No hay técnicos asignados para este día.</p>
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {assignmentsQuery.data?.map((assignment: Assignment) => (
                                <div key={assignment.id} className="flex min-w-0 flex-wrap items-center justify-between gap-3 p-3 border rounded-lg bg-card hover:bg-muted/10 transition-colors">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                                            {getUserName(assignment.technicianId).charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-medium">{getUserName(assignment.technicianId)}</p>
                                            <div className="mt-0.5 text-xs text-muted-foreground">
                                                <LaborDurationLabel assignment={assignment} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                                        {getStatusBadge(assignment.status)}

                                        {assignment.status === "working" || assignment.status === "paused" || assignment.status === "completed" ||
                                        assignment.activeStartTime != null || hasReliableDailyAssignmentStartMarker({
                                            id: assignment.id,
                                            installationId: assignment.installationId,
                                            status: assignment.status,
                                            activeStartTime: assignment.activeStartTime ?? null,
                                            totalMinutes: assignment.totalMinutes ?? null,
                                            startTime: assignment.startTime,
                                        }) ? (
                                            <span className="border-l pl-3 text-xs text-muted-foreground">Ejecución protegida</span>
                                        ) : (
                                            <div className="flex items-center border-l pl-3 gap-1">
                                                <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" title="Eliminar asignación" onClick={() => deleteMutation.mutate({ id: assignment.id })}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
        </div>
    );
}
